import type { HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, useMultiStyleConfig } from '@chakra-ui/react';
import { chakra } from '@chakra-ui/react';
import { useMemo } from 'react';

import { PromptBarProvider } from '../PromptBarContext';
import { PromptBarVariant } from '../types';
import {
  PromptBarStyles,
  PromptBarStylesProvider,
} from '../usePromptBarStyles';

export type PromptBarProps = HTMLChakraProps<'div'> & {
  /**
   * The variant of the Container.
   * The default variant is default.
   * The borderless variant is borderless.
   */
  variant?: PromptBarVariant;
  placeholder?: string;
  content: string;
  setContent: (content: string) => void;
  isDisabled?: boolean;
  isSubmissionDisabled?: boolean;
  isSubmitting?: boolean;
  onSubmit?: () => void;
  isGenerating?: boolean;
};

/**
 * A PromptBar is a combination of inputs and buttons enabling sending messages to an agent with context.
 */
export const PromptBar: React.FC<PromptBarProps> = forwardRef<
  PromptBarProps,
  'div'
>(
  (
    {
      variant,
      placeholder = 'Ask me anything...',
      content,
      setContent,
      isDisabled = false,
      isSubmissionDisabled = false,
      isSubmitting = false,
      onSubmit,
      isGenerating = false,
      children,
      ...props
    },
    ref,
  ) => {
    const promptBarStyles = useMultiStyleConfig('PromptBar', {
      variant,
    }) as PromptBarStyles;

    const value = useMemo(
      () => ({
        placeholder,
        content,
        setContent,
        isDisabled,
        isSubmissionDisabled,
        isSubmitting,
        onSubmit,
        isGenerating,
      }),
      [
        placeholder,
        content,
        setContent,
        isDisabled,
        isSubmissionDisabled,
        isSubmitting,
        onSubmit,
        isGenerating,
      ],
    );

    return (
      <PromptBarProvider value={value}>
        <PromptBarStylesProvider value={promptBarStyles}>
          <chakra.div
            sx={promptBarStyles.container}
            className="prompt-bar"
            {...props}
            ref={ref}
          >
            {children}
          </chakra.div>
        </PromptBarStylesProvider>
      </PromptBarProvider>
    );
  },
);
PromptBar.displayName = 'PromptBar';
